<template>
    <div class="dqc-wrapper">
        <div class="dqc-header">
            <div class="dqc-month-navigation">
                <button class="dqc-nav-btn" @click="previousMonth">
                    <div class="fa-2x text-inverse fas fa-angle-left"></div>
                </button>
                <span class="dqc-month-year">{{ currentYear }}.{{ String(currentMonth).padStart(2, "0") }}</span>
                <button class="dqc-nav-btn" @click="nextMonth">
                    <div class="fa-2x text-inverse fas fa-angle-right"></div>
                </button>
            </div>
        </div>
        <div class="dqc-grid">
            <div class="dqc-weekdays">
                <div class="dqc-weekday" v-for="day in weekdays" :key="day">{{ day }}</div>
            </div>
            <div class="dqc-days-grid">
                <div
                    v-for="(day, index) in calendarDays"
                    :key="index"
                    class="dqc-day-cell"
                    :class="{
                        'dqc-other-month': !day.isCurrentMonth,
                        'dqc-selected': day.date === selectedDate,
                        'dqc-today': day.isToday,
                        [`dqc-level-${day.level?.toLowerCase()}`]: day.level,
                        'dqc-loading': loading,
                    }"
                    @click="selectDate(day.date)"
                >
                    <div v-if="loading" class="dqc-skeleton">
                        <div class="dqc-skeleton-header">
                            <div class="dqc-skeleton-day"></div>
                            <div class="dqc-skeleton-badge"></div>
                        </div>
                    </div>
                    <div v-else class="dqc-day-header">
                        <div class="dqc-day-number-wrapper">
                            <div class="dqc-day-number" :class="{ 'dqc-today-number': day.isToday }">
                                {{ day.day }}
                            </div>
                        </div>
                        <div
                            v-if="day.rawDataRate !== undefined && day.rawDataRate !== null"
                            class="dqc-rate-badge"
                            :class="`dqc-rate-${day.level?.toLowerCase() || 'unknown'}`"
                        >
                            {{ day.rawDataRate?.toFixed(1) }}%
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import backEndApi from "@api/backEndApi.js";

export default {
    name: "DataQualityCalendar",
    props: {
        selectedDate: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            currentMonth: new Date().getMonth() + 1,
            currentYear: new Date().getFullYear(),
            calendarData: null,
            loading: false,
            weekdays: ["일", "월", "화", "수", "목", "금", "토"],
        };
    },
    computed: {
        calendarDays() {
            const days = [];
            const firstDay = new Date(this.currentYear, this.currentMonth - 1, 1);
            const lastDay = new Date(this.currentYear, this.currentMonth, 0);
            const startDate = new Date(firstDay);
            startDate.setDate(startDate.getDate() - firstDay.getDay());

            const endDate = new Date(lastDay);
            endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()));

            const today = new Date();
            const todayStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, "0")}${String(today.getDate()).padStart(2, "0")}`;

            for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
                const year = d.getFullYear();
                const month = d.getMonth() + 1;
                const day = d.getDate();
                const dateStr = `${year}${String(month).padStart(2, "0")}${String(day).padStart(2, "0")}`;
                const isCurrentMonth = month === this.currentMonth && year === this.currentYear;
                const isToday = dateStr === todayStr;

                // 현재 월의 데이터만 매칭 (년도와 월을 확인)
                let dayData = null;
                if (isCurrentMonth && this.calendarData?.days) {
                    const monthStr = `${this.currentYear}${String(this.currentMonth).padStart(2, "0")}`;
                    dayData = this.calendarData.days.find((dayItem) => {
                        const dayMonth = this.calendarData.month;
                        return dayMonth === monthStr && dayItem.day === String(day).padStart(2, "0");
                    });
                }

                days.push({
                    date: dateStr,
                    day: day,
                    isCurrentMonth,
                    isToday,
                    level: dayData?.overallQuality?.level,
                    rawDataRate: dayData?.overallQuality
                        ? Math.min(
                              dayData?.overallQuality?.rawDataRate,
                              dayData?.overallQuality?.summary1minRate,
                              dayData?.overallQuality?.summary15minRate
                          )
                        : null,
                });
            }

            return days;
        },
    },
    mounted() {
        this.loadCalendarData();
    },
    methods: {
        async loadCalendarData() {
            this.loading = true;
            try {
                const monthValue = parseInt(`${this.currentYear}${String(this.currentMonth).padStart(2, "0")}`);
                const response = await backEndApi.dataQuality.getDataQualityCalendar(monthValue, true);
                if (response.data && response.data.success) {
                    this.calendarData = response.data;
                }
            } catch (error) {
                console.error("캘린더 데이터 로드 실패:", error);
            } finally {
                this.loading = false;
            }
        },
        previousMonth() {
            if (this.currentMonth === 1) {
                this.currentMonth = 12;
                this.currentYear--;
            } else {
                this.currentMonth--;
            }
            this.loadCalendarData();
            this.$emit("month-changed", this.currentMonth, this.currentYear);
        },
        nextMonth() {
            if (this.currentMonth === 12) {
                this.currentMonth = 1;
                this.currentYear++;
            } else {
                this.currentMonth++;
            }
            this.loadCalendarData();
            this.$emit("month-changed", this.currentMonth, this.currentYear);
        },
        selectDate(date) {
            this.$emit("date-selected", date);
        },
        refreshCalendar() {
            this.loadCalendarData();
        },
    },
    watch: {
        currentMonth() {
            this.loadCalendarData();
        },
        currentYear() {
            this.loadCalendarData();
        },
    },
};
</script>

<style scoped>
.dqc-wrapper {
    width: 100%;
    height: 100%;
    padding: 16px;
    background: #ffffff;
}

.dqc-header {
    margin-bottom: 16px;
}

.dqc-month-navigation {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
}

.dqc-nav-btn {
    background: transparent;
    border: none;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 16px;
    color: #9ca3af;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    outline: none;
    line-height: 1;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    font-weight: 500;
}

.dqc-nav-btn:focus {
    outline: none;
}

.dqc-nav-btn:hover {
    background: #f9fafb;
    color: #6b7280;
}

.dqc-nav-btn:active {
    background: #f3f4f6;
    transform: scale(0.95);
}

.dqc-month-year {
    font-size: 16px;
    font-weight: 600;
    color: #374151;
    text-align: center;
    letter-spacing: -0.3px;
}

.dqc-grid {
    width: 100%;
}

.dqc-weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
    margin-bottom: 12px;
}

.dqc-weekday {
    text-align: center;
    padding: 6px;
    font-size: 15px;
    font-weight: 600;
    color: #64748b;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.dqc-days-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
    background: #f1f5f9;
    padding: 2px;
    border-radius: 12px;
}

.dqc-day-cell {
    background: linear-gradient(135deg, #ffffff 0%, #fefefe 100%);
    min-height: 100px;
    padding: 12px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    display: flex;
    flex-direction: column;
    border-radius: 10px;
    border: 1px solid transparent;
    outline: none;
}

.dqc-day-cell:focus {
    outline: none;
}

.dqc-day-cell:active {
    outline: none;
}

.dqc-day-cell:hover {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    z-index: 1;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: #e2e8f0;
}

.dqc-day-cell.dqc-other-month {
    background: linear-gradient(135deg, #fafbfc 0%, #f8f9fa 100%);
    color: #cbd5e1;
    opacity: 0.6;
}

.dqc-day-cell.dqc-selected {
    border: 2px solid #94a3b8;
}

.dqc-day-cell.dqc-selected:focus {
    outline: none;
}

.dqc-day-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    margin-bottom: 6px;
}

.dqc-day-number-wrapper {
    display: flex;
    align-items: center;
    gap: 4px;
    position: relative;
}

.dqc-day-number {
    font-size: 16px;
    font-weight: 700;
    color: #1e293b;
    position: relative;
    display: inline-block;
}

.dqc-day-cell.dqc-other-month .dqc-day-number {
    color: #9ca3af;
}

.dqc-day-number.dqc-today-number {
    border-bottom: 2px solid #64748b;
    padding-bottom: 1px;
}

.dqc-rate-badge {
    font-size: 12px;
    font-weight: 600;
    padding: 2px 5px;
    border-radius: 4px;
    white-space: nowrap;
}

.dqc-rate-error {
    background: #fee2e2;
    color: #991b1b;
}

.dqc-rate-critical {
    background: #fed7aa;
    color: #9a3412;
}

.dqc-rate-warning {
    background: #fef3c7;
    color: #92400e;
}

.dqc-rate-normal {
    background: #d1fae5;
    color: #065f46;
}

.dqc-rate-unknown {
    background: #f3f4f6;
    color: #374151;
}

.dqc-bad-meters-badge {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: #ffffff;
    font-size: 12px;
    font-weight: 700;
    padding: 4px 8px;
    border-radius: 50%;
    min-width: 16px;
    text-align: center;
    box-shadow: 0 1px 3px rgba(239, 68, 68, 0.3);
    line-height: 1.2;
}

.dqc-day-cell.dqc-level-error {
    background: linear-gradient(135deg, #ffffff 0%, #fef2f2 100%);
}

.dqc-day-cell.dqc-level-critical {
    background: linear-gradient(135deg, #ffffff 0%, #fef2f2 100%);
}

.dqc-day-cell.dqc-level-warning {
    background: linear-gradient(135deg, #ffffff 0%, #fffbeb 100%);
}

.dqc-day-cell.dqc-level-normal {
    background: linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%);
}

.dqc-day-cell.dqc-level-unknown {
    background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%);
}

.dqc-day-cell.dqc-loading {
    cursor: default;
    pointer-events: none;
}

.dqc-day-cell.dqc-loading:hover {
    transform: none;
    box-shadow: none;
}

.dqc-skeleton {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.dqc-skeleton-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
}

.dqc-skeleton-day {
    width: 24px;
    height: 20px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: dqc-skeleton-loading 1.5s ease-in-out infinite;
    border-radius: 4px;
}

.dqc-skeleton-badge {
    width: 40px;
    height: 16px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: dqc-skeleton-loading 1.5s ease-in-out infinite;
    border-radius: 4px;
}

@keyframes dqc-skeleton-loading {
    0% {
        background-position: 200% 0;
    }

    100% {
        background-position: -200% 0;
    }
}
</style>
